import styled, { css } from 'styled-components'

export const RfViewport = styled.main<{ $embedded?: boolean }>`
  display: flex;
  justify-content: center;
  color: ${(props) => props.theme.colors.riverBed};

  ${(props) =>
    props.$embedded
      ? css`
          width: 100%;
          height: 100%;
          min-height: 0;
          align-items: stretch;
          background: transparent;
        `
      : css`
          height: 100vh;
          height: 100dvh;
          min-height: 0;
          background: #e8eaf1;

          @media (min-width: 520px) {
            align-items: center;
            padding: 16px 0;
          }
        `}
`

export const RfSurface = styled.section<{ $embedded?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${(props) => props.theme.colors.white};

  ${(props) =>
    props.$embedded
      ? css`
          height: 100%;
          min-height: 0;
          max-height: none;
          border-radius: 8px;
          box-shadow: none;
        `
      : css`
          height: 100vh;
          height: 100dvh;
          min-height: 0;

          @media (min-width: 520px) {
            height: calc(100dvh - 32px);
            min-height: 0;
            max-height: 800px;
            border-radius: 8px;
            box-shadow: 0 0 32px rgba(30, 34, 55, 0.16);
          }
        `}
`

export const RfHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  min-height: 48px;
  display: grid;
  grid-template-columns: 72px 1fr 72px;
  align-items: center;
  background: ${(props) => props.theme.colors.darkPurple};
  color: ${(props) => props.theme.colors.white};
`

export const HeaderButton = styled.button`
  min-width: 48px;
  min-height: 48px;
  margin: 0 4px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  font-size: 0.92rem;
  font-weight: 600;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.14);
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.colors.lightOrange};
    outline-offset: -3px;
  }
`

export const HeaderSpacer = styled.span`
  display: block;
  width: 72px;
`

export const HeaderTitle = styled.div`
  min-width: 0;
  text-align: center;

  span {
    display: block;
    margin-bottom: 1px;
    color: rgba(255, 255, 255, 0.76);
    font-size: 0.66rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    line-height: 1;
    text-transform: uppercase;
  }

  strong {
    display: block;
    overflow: hidden;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const ContextToggle = styled.button`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 16px;
  border: 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.gallery};
  background: ${(props) => props.theme.colors.linkWater};
  color: ${(props) => props.theme.colors.riverBed};
  text-align: left;

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.colors.dodgerBlue};
    outline-offset: -3px;
  }
`

export const ContextBar = styled.div`
  min-height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 6px 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.gallery};
  background: ${(props) => props.theme.colors.linkWater};
  color: ${(props) => props.theme.colors.riverBed};
`

export const ContextSummary = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.35;
`

export const ContextToggleLabel = styled.span`
  flex: 0 0 auto;
  color: ${(props) => props.theme.colors.dodgerBlue};
  font-size: 0.8rem;
  font-weight: 600;
`

export const ContextPanel = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.gallery};
  background: ${(props) => props.theme.colors.whiteLilac};
`

export const ContextGrid = styled.dl`
  display: grid;
  grid-template-columns: minmax(88px, auto) 1fr;
  gap: 8px 14px;
  margin: 0;

  dt,
  dd {
    margin: 0;
  }

  dt {
    color: ${(props) => props.theme.colors.raven};
    font-size: 0.78rem;
    font-weight: 500;
    text-transform: uppercase;
  }

  dd {
    font-size: 0.9rem;
    font-weight: 600;
    text-align: right;
  }
`

export const RfMain = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 12px 16px;
`

export const TaskHeader = styled.div`
  margin-bottom: 10px;

  h1 {
    margin: 0 0 4px;
    color: ${(props) => props.theme.colors.riverBed};
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.2;
  }

  p {
    margin: 0;
    color: ${(props) => props.theme.colors.raven};
    font-size: 0.9rem;
    line-height: 1.4;
  }
`

export const ScanForm = styled.form`
  label {
    margin-bottom: 8px;
    color: ${(props) => props.theme.colors.riverBed};
    font-size: 0.9rem;
    font-weight: 600;
  }

  .input-group-text {
    min-width: 52px;
    justify-content: center;
    border-color: ${(props) => props.theme.colors.periwinkleGray};
    background: ${(props) => props.theme.colors.ghostWhite};
    color: ${(props) => props.theme.colors.darkPurple};
    font-size: 1.15rem;
  }

  .form-control {
    min-height: 56px;
    border-color: ${(props) => props.theme.colors.periwinkleGray};
    font-size: 1.08rem;
    font-weight: 500;
  }

  .form-control:focus {
    border-color: ${(props) => props.theme.colors.dodgerBlue};
    box-shadow: 0 0 0 3px rgba(93, 120, 255, 0.2);
  }
`

export const FieldHint = styled.p`
  margin: 8px 0 0;
  color: ${(props) => props.theme.colors.raven};
  font-size: 0.84rem;
  line-height: 1.45;
`

export const MenuList = styled.div`
  display: grid;
  gap: 12px;
`

export const VariantActionList = styled(MenuList)`
  margin-top: 0;
  gap: 6px;
  button {
    min-height: 48px;
    padding: 8px 12px;
    gap: 2px;
  }
  button strong {
    font-size: 1rem;
  }
  button span {
    font-size: 0.8rem;
    line-height: 1.3;
  }
`

export const VariantProductName = styled.p`
  margin: 0 0 8px;
  font-size: 0.92rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
`

export const MenuButton = styled.button`
  width: 100%;
  min-height: 64px;
  display: grid;
  gap: 5px;
  align-content: center;
  padding: 16px;
  border: 1px solid ${(props) => props.theme.colors.periwinkleGray};
  border-radius: 6px;
  background: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.riverBed};
  text-align: left;

  strong {
    font-size: 1.1rem;
    font-weight: 700;
  }

  span {
    color: ${(props) => props.theme.colors.raven};
    font-size: 0.9rem;
    line-height: 1.4;
  }

  &:hover:not(:disabled) {
    border-color: ${(props) => props.theme.colors.dodgerBlue};
    background: ${(props) => props.theme.colors.ghostWhite};
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.colors.dodgerBlue};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }
`

export const CandidateList = styled.div`
  display: grid;
  gap: 8px;
  margin-bottom: 10px;
`

export const CandidateButton = styled.button<{ $selected?: boolean }>`
  width: 100%;
  display: grid;
  gap: 4px;
  min-height: 48px;
  padding: 8px 12px;
  border: 2px solid ${(props) => (props.$selected ? props.theme.colors.dodgerBlue : props.theme.colors.periwinkleGray)};
  border-radius: 6px;
  background: ${(props) => (props.$selected ? props.theme.colors.ghostWhite : props.theme.colors.white)};
  color: ${(props) => props.theme.colors.riverBed};
  text-align: left;

  strong {
    font-size: 1rem;
  }

  span {
    color: ${(props) => props.theme.colors.raven};
    font-size: 0.82rem;
    line-height: 1.35;
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.colors.dodgerBlue};
    outline-offset: 2px;
  }
`

export const WarningTaskButton = styled.button`
  width: 100%;
  min-height: 48px;
  margin-top: 8px;
  padding: 10px 14px;
  border: 1px solid ${(props) => props.theme.colors.mySin};
  border-radius: 5px;
  background: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.riverBed};
  font-size: 0.92rem;
  font-weight: 700;

  &:hover,
  &:focus-visible {
    background: rgba(255, 184, 34, 0.12);
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.colors.lightOrange};
    outline-offset: 2px;
  }
`

export const AsnSummaryCard = styled.div`
  overflow: hidden;
  border: 1px solid ${(props) => props.theme.colors.periwinkleGray};
  border-radius: 7px;
  background: ${(props) => props.theme.colors.white};
`

export const AsnSummaryHeader = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${(props) => props.theme.colors.gallery};
  background: ${(props) => props.theme.colors.linkWater};

  strong {
    display: block;
    margin-bottom: 3px;
    font-size: 1.08rem;
  }

  span {
    color: ${(props) => props.theme.colors.raven};
    font-size: 0.85rem;
  }
`

export const AsnSummaryDetails = styled.dl`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px 12px;
  margin: 0;
  padding: 12px;

  dt,
  dd {
    margin: 0;
  }

  dt {
    color: ${(props) => props.theme.colors.raven};
    font-size: 0.82rem;
  }

  dd {
    font-size: 0.88rem;
    font-weight: 700;
    text-align: right;
  }
`

export const PreprocessChoiceList = styled.div`
  display: grid;
  gap: 12px;
`

export const PhotoEvidenceField = styled.div`
  display: grid;
  gap: 10px;
  padding: 12px;
  border: 1px solid ${(props) => props.theme.colors.periwinkleGray};
  border-radius: 8px;
  background: ${(props) => props.theme.colors.linkWater};

  h2 {
    margin: 0;
    font-size: 1rem;
  }

  ul {
    display: grid;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    padding: 8px 10px;
    border-radius: 6px;
    background: ${(props) => props.theme.colors.white};
    overflow-wrap: anywhere;
    font-size: 0.88rem;
  }

  p {
    margin: 0;
    color: ${(props) => props.theme.colors.raven};
    font-size: 0.86rem;
    line-height: 1.4;
  }

  > button {
    min-height: 48px;
    padding: 10px 12px;
    text-transform: none;
    letter-spacing: normal;
  }
`

export const PhotoEvidenceHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  svg {
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    color: ${(props) => props.theme.colors.darkPurple};
  }
`

export const PhotoActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  button {
    min-width: 0;
    min-height: 48px;
    padding: 10px 8px;
    border-radius: 6px;
    font-size: 0.9rem;
    text-transform: none;
    letter-spacing: normal;
  }
`

export const LabelPlanList = styled.ul`
  display: grid;
  gap: 6px;
  margin: 10px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 12px;
    border: 1px solid ${(props) => props.theme.colors.gallery};
    border-radius: 5px;
    background: ${(props) => props.theme.colors.whiteLilac};
  }

  span {
    color: ${(props) => props.theme.colors.raven};
  }
`

export const LabelGroupCard = styled.section`
  display: grid;
  gap: 6px;
  margin-bottom: 8px;
  padding: 10px 12px;
  border: 1px solid ${(props) => props.theme.colors.periwinkleGray};
  border-radius: 8px;
  h2 {
    margin: 0;
    font-size: 1.1rem;
  }
  p {
    margin: 0;
  }
  summary {
    min-height: 44px;
    cursor: pointer;
    padding: 10px 0;
  }
  li {
    overflow-wrap: anywhere;
    font-family: inherit;
    font-weight: normal;
    line-height: 1.5;
  }
`

export const PrintGroupCard = styled(LabelGroupCard)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 70px;
  h2 {
    font-size: 1rem;
  }
  > button {
    flex: 0 0 auto;
    width: auto;
    min-width: 80px;
    min-height: 48px;
    padding: 8px 12px;
    text-transform: none;
    letter-spacing: normal;
  }
`

export const LabelProcessingHeading = styled.div`
  margin-bottom: 12px;
  h1 {
    margin: 0 0 4px;
    font-size: 1.25rem;
    line-height: 1.3;
  }
  p {
    margin: 0;
    font-size: 0.85rem;
    color: ${(props) => props.theme.colors.raven};
  }
`

export const LabelGroupHeading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  gap: 4px;
  strong {
    flex-shrink: 0;
    font-size: 1.1rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`

export const PrintProgress = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
  strong {
    font-size: 1rem;
    font-variant-numeric: tabular-nums;
  }
  .check {
    color: #126b5f;
    font-size: 0.9rem;
  }
  .label {
    color: ${(props) => props.theme.colors.raven};
    font-size: 0.75rem;
    font-weight: 400;
  }
`

export const ReceiptMetric = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  span {
    font-size: 0.85rem;
    color: ${(props) => props.theme.colors.raven};
  }
  strong {
    font-size: 1.1rem;
    font-variant-numeric: tabular-nums;
  }
`

export const InputSummary = styled.div`
  margin-bottom: 12px;
  > p {
    margin: 6px 0 0;
  }
  > p:first-child {
    margin-top: 0;
  }
`

export const QuantityLedger = styled.dl`
  margin: 0;
  display: grid;
  gap: 4px;
  div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }
  dt {
    position: relative;
    padding-left: 16px;
    font-size: 0.88rem;
    font-weight: 400;
  }
  dt > span {
    position: absolute;
    left: 0;
  }
  dd {
    margin: 0;
    font-size: 0.95rem;
    font-variant-numeric: tabular-nums;
  }
  .subtotal,
  .total {
    border-top: 1px solid ${(props) => props.theme.colors.periwinkleGray};
    padding-top: 6px;
    margin-top: 2px;
  }
  .total dt,
  .total dd {
    font-size: 1rem;
    font-weight: 700;
  }
  .total dd span {
    color: #126b5f;
  }
`

export const DialogBackdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(16, 18, 29, 0.62);
`

export const DecisionDialog = styled.div`
  width: 100%;
  max-width: 400px;
  max-height: 100%;
  overflow-y: auto;
  padding: 20px;
  border-radius: 10px;
  background: ${(props) => props.theme.colors.white};
  box-shadow: 0 18px 48px rgba(16, 18, 29, 0.28);

  h2 {
    margin: 0 0 10px;
    font-size: 1.25rem;
  }

  p {
    margin: 0;
    color: ${(props) => props.theme.colors.raven};
    line-height: 1.5;
  }
`

export const DecisionNotice = styled.div`
  margin: 16px 0;
  padding: 11px 12px;
  border-left: 4px solid ${(props) => props.theme.colors.mySin};
  border-radius: 4px;
  background: #fff8e8;
  color: #765208;
  font-size: 0.84rem;
  line-height: 1.45;
`

export const DecisionActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 18px;
`

export const DecisionButton = styled.button<{ $primary?: boolean }>`
  min-height: 46px;
  border: 1px solid ${(props) => (props.$primary ? props.theme.colors.darkPurple : props.theme.colors.periwinkleGray)};
  border-radius: 5px;
  background: ${(props) => (props.$primary ? props.theme.colors.darkPurple : props.theme.colors.white)};
  color: ${(props) => (props.$primary ? props.theme.colors.white : props.theme.colors.riverBed)};
  font-weight: 700;

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.colors.lightOrange};
    outline-offset: 2px;
  }
`

export const DeclineButton = styled(DecisionButton)`
  background: ${(props) => props.theme.colors.crimson};
  border-color: ${(props) => props.theme.colors.crimson};
  color: ${(props) => props.theme.colors.white};
`

const statusStyles = {
  success: css`
    border-color: ${(props) => props.theme.colors.emeraldGreen};
    background: #edfbf8;
    color: #126b5f;
  `,
  warning: css`
    border-color: ${(props) => props.theme.colors.mySin};
    background: #fff8e8;
    color: #765208;
  `,
  error: css`
    border-color: ${(props) => props.theme.colors.crimson};
    background: #fff1f1;
    color: #921010;
  `,
  info: css`
    border-color: ${(props) => props.theme.colors.dodgerBlue};
    background: ${(props) => props.theme.colors.ghostWhite};
    color: #243989;
  `,
}

export const StatusBanner = styled.div<{ $tone: keyof typeof statusStyles }>`
  margin-bottom: 8px;
  padding: 8px 10px;
  border: 1px solid;
  border-left-width: 4px;
  border-radius: 4px;
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.45;
  ${(props) => statusStyles[props.$tone]}
`

export const QuantitySummary = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin: 0 0 8px;
  div {
    padding: 8px;
    border-radius: 5px;
    background: ${(props) => props.theme.colors.linkWater};
  }
  dt {
    font-size: 0.75rem;
    font-weight: 500;
  }
  dd {
    margin: 2px 0 0;
    font-size: 1rem;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
`

export const CompactDetails = styled.details`
  margin: 0;
  summary {
    min-height: 44px;
    padding: 10px 0;
    font-size: 0.85rem;
    cursor: pointer;
  }
`

export const ActionBar = styled.footer`
  position: sticky;
  bottom: 0;
  z-index: 15;
  display: grid;
  gap: 10px;
  padding: 12px 16px max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid ${(props) => props.theme.colors.gallery};
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 -8px 20px rgba(30, 34, 55, 0.08);
`

export const ActionButton = styled.button<{ $secondary?: boolean }>`
  width: 100%;
  min-height: 56px;
  padding: 12px 16px;
  border: 1px solid ${(props) => props.theme.colors.dodgerBlue};
  border-radius: 5px;
  background: ${(props) => (props.$secondary ? props.theme.colors.white : props.theme.colors.dodgerBlue)};
  color: ${(props) => (props.$secondary ? props.theme.colors.dodgerBlue : props.theme.colors.white)};
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background: ${(props) => (props.$secondary ? props.theme.colors.zircon : props.theme.colors.dodgerBlueLight)};
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.colors.lightOrange};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

export const InlineSecondaryAction = styled(ActionButton)`
  margin-top: 12px;
  min-height: 48px;
  font-size: 0.9rem;
  text-transform: none;
  letter-spacing: normal;
`

export const OperationalContext = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 12px;
  margin: 0 0 8px;
  padding: 0 0 6px;
  border-bottom: 1px solid ${(props) => props.theme.colors.gallery};
  div {
    min-width: 0;
  }
  dt {
    color: ${(props) => props.theme.colors.raven};
    font-size: 0.72rem;
    font-weight: 500;
  }
  dd {
    margin: 1px 0 0;
    font-size: 0.84rem;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`

export const SheetBackdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 40;
  background: rgba(16, 18, 29, 0.58);
`

export const ActionSheet = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  z-index: 41;
  width: 100%;
  padding: 8px 16px max(16px, env(safe-area-inset-bottom));
  border-radius: 14px 14px 0 0;
  background: ${(props) => props.theme.colors.white};
  box-shadow: 0 -12px 32px rgba(16, 18, 29, 0.24);
  transform: translateX(-50%);

  h2 {
    margin: 8px 0 12px;
    color: ${(props) => props.theme.colors.riverBed};
    font-size: 1.05rem;
    font-weight: 600;
    text-align: center;
  }
`

export const SheetAction = styled.button<{ $danger?: boolean }>`
  width: 100%;
  min-height: 54px;
  padding: 12px 10px;
  border: 0;
  border-top: 1px solid ${(props) => props.theme.colors.gallery};
  background: transparent;
  color: ${(props) => (props.$danger ? props.theme.colors.crimson : props.theme.colors.riverBed)};
  font-size: 1rem;
  font-weight: 600;
  text-align: left;

  &:hover,
  &:focus-visible {
    background: ${(props) => props.theme.colors.whiteLilac};
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.colors.dodgerBlue};
    outline-offset: -3px;
  }
`
